import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { cn } from '@/shared/lib/cn';

/**
 * The signed-in person, at the foot of the rail.
 *
 * <h2>Why this is a link and not a second dropdown</h2>
 *
 * `AccountMenu` in the top bar already owns the account dropdown — the profile, billing, My
 * Account and signing out. A second menu here would be two implementations of one thing, and
 * the one that fell behind would be the one somebody used. This opens `/account`, which is
 * where the redesign puts account access, and the dropdown above keeps the rest.
 *
 * <h2>Real session data, passed in rather than read</h2>
 *
 * The person arrives as a prop because **`AppSidebar` reads no context** — it takes
 * `roleKeys` and `productKeys` from the layout for the same reason, so the rail is a function
 * of its arguments and can be rendered in a test without a provider around it. Calling
 * `useAuth` here broke eleven of those tests, which is how the contract was found.
 *
 * <p>Null while the session is in flight, and then this renders nothing rather than a
 * skeleton carrying somebody else's name.
 *
 * <h2>The name, and not the address</h2>
 *
 * The block showed both until 8 October 2026 — two lines, 44px of rail, and the address
 * repeated from the account menu directly above the rail's own foot. The source shows the
 * name alone beside the initials, which is also what makes the row the same height as
 * "Settings & help" above it rather than half again as tall.
 */
export interface SidebarUserProfileProps {
  collapsed: boolean;
  user: SidebarUser | null;
}

/** The three fields the block draws. Narrower than the session's user on purpose. */
export interface SidebarUser {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

/** Initials from the name, falling back to the address — the same rule `AccountMenu` uses. */
function initialsOf(firstName?: string | null, lastName?: string | null, email?: string): string {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.trim();
  if (initials) {
    return initials.toUpperCase();
  }
  return (email?.[0] ?? '?').toUpperCase();
}

export function SidebarUserProfile({ collapsed, user }: SidebarUserProfileProps) {
  if (!user) {
    return null;
  }

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;

  return (
    <Link
      to={ROUTES.account}
      title={collapsed ? displayName : undefined}
      className={cn(
        'flex items-center rounded-xl border-t border-gray-100 pt-3 text-sm font-medium text-gray-900 transition-colors hover:text-gray-950 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:border-white/10 dark:text-gray-200',
        collapsed ? 'justify-center' : 'gap-3 px-3',
      )}
    >
      <span
        aria-hidden
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700 dark:bg-blue-500/20 dark:text-blue-300"
      >
        {initialsOf(user.firstName, user.lastName, user.email)}
      </span>

      {collapsed ? null : (
        <>
          <span className="min-w-0 flex-1 truncate">{displayName}</span>
          <ChevronRight aria-hidden className="h-4 w-4 flex-shrink-0 text-gray-400" />
        </>
      )}
    </Link>
  );
}
